import React from 'react';
import Drawer from 'react-modern-drawer';
import 'react-modern-drawer/dist/index.css';
import styled from 'styled-components';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import Button from '../../components/ui/Button';
import { formatDate } from '../../utils/Utils';
import {
  positionModeOf, requisitionPositionText, seatProgressText,
} from './requisitionActions';
import RecruitingCostsSection from './RecruitingCostsSection';
import BandDeviationBadge from './BandDeviationBadge';
import PoolSuggestionsSection from '../ats-candidate-pools/components/PoolSuggestionsSection';

// FE-17 — SALT OKUNUR kadro talebi detayı.
//
// Neden GenericFormDrawer DEĞİL: o bileşen her zaman bir "Kaydet" ayağı çizer
// ve alanları düzenlenebilir kılar; burada gösterilen talep onay bekliyor ve
// içeriği DONDURULMUŞ olmalı (düzenleme backend'de talebi taslağa geri
// döndürür). Ev kuralı "form açmak için GenericFormDrawer" — bu bir form değil.
// Çekmece kabuğu (react-modern-drawer + aynı başlık/gövde/ayak ölçüleri)
// GenericFormDrawer'ın birebir aynısıdır, tasarım dili ayrışmasın diye.
//
// Onaycı burada hem GEREKÇEYİ okur hem KARAR VERİR: ayakta Onayla / Reddet,
// satır-içi düğmelerle AYNI diyalogları açar (tek kaynak: requisitionActions).

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #E8ECF0;
`;

const Title = styled.h3`
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  margin: 0;
`;

const CloseBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: none;
  color: ${({ theme }) => theme.colors.text.dark.little};
  cursor: pointer;
  &:hover { background: ${({ theme }) => theme.colors.bgColor}; }
`;

// TEK KAYDIRMA: gövde kendi içinde kayar, sayfa kaymaz. `min-height: 0`
// atlanırsa flex çocuğu küçülmez ve taşma sayfaya döner (ev kuralı).
const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 20px 24px;
`;

const Footer = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  padding: 16px 24px;
  border-top: 1px solid #E8ECF0;
`;

const SectionTitle = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.text.dark.little};
  margin: 22px 0 10px;
  &:first-child { margin-top: 0; }
`;

const Rows = styled.dl`
  margin: 0;
  display: grid;
  grid-template-columns: minmax(120px, 40%) 1fr;
  gap: 8px 16px;
`;

const Label = styled.dt`
  margin: 0;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const Value = styled.dd`
  margin: 0;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  word-break: break-word;
`;

const LongText = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: pre-wrap;
  word-break: break-word;
  background: ${({ theme }) => theme.colors.bgColor};
  border-radius: 6px;
  padding: 12px 14px;
`;

const DANGER_TEXT = '#B3261E';

const RejectedText = styled(LongText)`
  color: ${DANGER_TEXT};
`;

const dash = (v) => (v === null || v === undefined || v === '' ? '-' : v);

export function requisitionSalaryBand(row, locale = 'tr') {
  if (!row) return '-';
  const { minSalary, maxSalary, salaryCurrency } = row;
  if (minSalary == null && maxSalary == null) return '-';
  const fmt = (n) => (n == null ? '?' : Number(n).toLocaleString(locale));
  return `${fmt(minSalary)} - ${fmt(maxSalary)}${salaryCurrency ? ` ${salaryCurrency}` : ''}`;
}

// Tek kaynak requisitionActions — liste kolonu ile aynı metin.
export const requisitionPositionLabel = requisitionPositionText;

// SPEC F3 — işe alım maliyeti bölümünün kimliği kadro satırından okunur.
// Satırda `hiredCount` yoksa koltuk modelli talepte dolu koltuk sayısı; legacy
// satırda bilinmez (null → işe alım başına maliyet "—", sunucu özeti varsa o kazanır).
function hiredCountOf(row) {
  if (!row) return null;
  if (row.hiredCount != null) return row.hiredCount;
  return positionModeOf(row) === 'legacy' ? null : (row.seatsFilled ?? null);
}

// `costAccess`: { writable, ownerIds } — maliyet okuma AT.Write VEYA kadronun
// HM'i; yazma YALNIZ AT.Write (HM salt okunur).
const POOL_SUGGESTION_STATUSES = ['approved', 'posting_opened', 'on_hold'];

export default function RequisitionDetailDrawer({
  open, row, onClose, actions = [], costAccess = null,
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const d = (v) => (v ? formatDate(v, lang) : '-');
  const enumLabel = (ns, v) => (v ? t(`ats.requisition.${ns}.${v}`, { defaultValue: v }) : '-');
  const mode = positionModeOf(row);
  const modern = mode === 'existing' || mode === 'new';
  const costWritable = !!costAccess?.writable;
  const costReadable = !!row && (costWritable
    || (row.hiringManagerUserId != null && (costAccess?.ownerIds || []).includes(row.hiringManagerUserId)));

  return (
    <Drawer
      open={open}
      onClose={onClose}
      direction="right"
      size="45%"
      duration={150}
      overlayOpacity={0.2}
      style={{
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '100vw',
        minWidth: 380,
        margin: '12px',
        borderRadius: '6px',
        height: 'calc(100vh - 24px)',
        boxShadow: '0 8px 32px rgba(0, 66, 66, 0.12)',
      }}
    >
      <Header>
        <Title>{t('ats.requisition.detailTitle')}</Title>
        <CloseBtn onClick={onClose} aria-label={t('common.close')}><X size={20} /></CloseBtn>
      </Header>

      <Body>
        {open && row && (
          <>
            <SectionTitle>{t('ats.requisition.pageTitle')}</SectionTitle>
            <Rows>
              {/* Talep türü yalnız pozisyon modelli taleplerde; legacy satır eski görünümde. */}
              {modern && (
                <>
                  <Label>{t('ats.requisition.positionMode.label')}</Label>
                  <Value data-role="ats-requisition-mode">{t(`ats.requisition.positionMode.${mode}`)}</Value>
                </>
              )}
              {mode === 'new' ? (
                <>
                  <Label>{t('ats.requisition.parentPositionId')}</Label>
                  <Value>{row.parentPositionCode ? `[${row.parentPositionCode}]` : '-'}</Value>
                </>
              ) : (
                <>
                  <Label>{t('ats.requisition.positionId')}</Label>
                  <Value>{requisitionPositionLabel(row)}</Value>
                </>
              )}
              <Label>{t('ats.requisition.departmentId')}</Label>
              <Value>{dash(row.departmentName)}</Value>
              <Label>{t('ats.requisition.location')}</Label>
              <Value>{dash(row.location)}</Value>
              <Label>{t('ats.requisition.statusLabel')}</Label>
              <Value>{t(`ats.requisition.status.${row.status}`, { defaultValue: row.status })}</Value>
              <Label>{t('ats.requisition.requestedHeadcount')}</Label>
              <Value>{dash(row.requestedHeadcount)}</Value>
              <Label>{t('ats.requisition.approvedHeadcount')}</Label>
              <Value>{dash(row.approvedHeadcount)}</Value>
              {mode === 'new' && (
                <>
                  <Label>{t('ats.requisition.openedPositionCount')}</Label>
                  <Value data-role="ats-requisition-opened-count">{row.seatsTotal ?? 0}</Value>
                </>
              )}
              {modern && (
                <>
                  <Label>{t('ats.requisition.seatsColumn')}</Label>
                  <Value data-role="ats-requisition-seats">{seatProgressText(row, t)}</Value>
                </>
              )}
              <Label>{t('ats.requisition.jobPostingCount')}</Label>
              <Value>{row.jobPostingCount ?? 0}</Value>
            </Rows>

            <SectionTitle>{t('ats.requisition.peopleSection')}</SectionTitle>
            <Rows>
              <Label>{t('ats.requisition.hiringManagerUserId')}</Label>
              <Value>{dash(row.hiringManagerName)}</Value>
              <Label>{t('ats.requisition.hrBpUserId')}</Label>
              <Value>{dash(row.hrBpName)}</Value>
            </Rows>

            <SectionTitle>{t('ats.requisition.conditionsSection')}</SectionTitle>
            <Rows>
              <Label>{t('ats.requisition.salaryBand')}</Label>
              <Value>
                {requisitionSalaryBand(row, lang)}
                <BandDeviationBadge deviation={row.salaryBandDeviation} />
              </Value>
              <Label>{t('ats.requisition.contractType.label')}</Label>
              <Value>{enumLabel('contractType', row.contractType)}</Value>
              <Label>{t('ats.requisition.employmentType.label')}</Label>
              <Value>{enumLabel('employmentType', row.employmentType)}</Value>
              <Label>{t('ats.requisition.remoteType.label')}</Label>
              <Value>{enumLabel('remoteType', row.remoteType)}</Value>
            </Rows>

            <RecruitingCostsSection
              requisitionId={row.id}
              canRead={costReadable}
              canWrite={costWritable}
              hiredCount={hiredCountOf(row)}
            />

            {/* Aday Havuzu önerileri — havuzlar yalnız AT.Write/admin'e açıktır (tüm adayları
                içerir); öneri yalnız aday aranabilecek açık kadrolarda anlamlıdır. */}
            {costWritable && POOL_SUGGESTION_STATUSES.includes(row.status) && (
              <PoolSuggestionsSection requisitionId={row.id} />
            )}

            <SectionTitle>{t('ats.requisition.justification')}</SectionTitle>
            <LongText>{row.justificationText || '-'}</LongText>

            {row.rejectedReason && (
              <>
                <SectionTitle>{t('ats.requisition.rejectReason')}</SectionTitle>
                <RejectedText>{row.rejectedReason}</RejectedText>
              </>
            )}

            <SectionTitle>{t('ats.requisition.timeline')}</SectionTitle>
            <Rows>
              <Label>{t('ats.requisition.createDate')}</Label>
              <Value>{d(row.createDate)}</Value>
              <Label>{t('ats.requisition.submittedAt')}</Label>
              <Value>{d(row.submittedAt)}</Value>
              <Label>{t('ats.requisition.approvedAt')}</Label>
              <Value>{d(row.approvedAt)}</Value>
              <Label>{t('ats.requisition.rejectedAt')}</Label>
              <Value>{d(row.rejectedAt)}</Value>
              <Label>{t('ats.requisition.filledAt')}</Label>
              <Value>{d(row.filledAt)}</Value>
              <Label>{t('ats.requisition.closedAt')}</Label>
              <Value>{d(row.closedAt)}</Value>
            </Rows>
          </>
        )}
      </Body>

      <Footer>
        <Button variant="outline" onClick={onClose}>{t('common.close')}</Button>
        {actions.map((a) => (
          <Button key={a.action} variant={a.variant} onClick={a.onClick}>{a.label}</Button>
        ))}
      </Footer>
    </Drawer>
  );
}
